import { useQueryClient } from "@tanstack/react-query";
import {
  CalendarDays,
  CircleHelp,
  History,
  Home,
  LogOut,
  Menu,
  Settings,
  Sparkles,
  Users,
} from "lucide-react";
import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { useTourGuide, VISIT_TOUR_ID } from "@src/features/tourguide";
import { PRODUCT_NAME } from "@src/shared/brand";
import { Avatar, AvatarFallback } from "@src/shared/components/ui/avatar";
import { Button } from "@src/shared/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@src/shared/components/ui/popover";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@src/shared/components/ui/sheet";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@src/shared/components/ui/tooltip";
import { cn } from "@src/shared/lib/utils";
import { navigationV2 } from "@src/shared/navigation/v2";

import { logout } from "../../../../application";
import type {
  AuthenticatedLayoutUserV2,
  AuthenticatedSidebarItemV2,
} from "./AuthenticatedLayout.types";

const routes = [
  {
    id: "home",
    label: "Home",
    href: navigationV2.home(),
    icon: Home,
  },
  {
    id: "visits",
    label: "Visits",
    href: navigationV2.visits(),
    icon: CalendarDays,
  },
  {
    id: "patients",
    label: "Patients",
    href: navigationV2.patients(),
    icon: Users,
  },
  {
    id: "documents",
    label: "Upload History",
    href: navigationV2.documents(),
    icon: History,
  },
];

interface NavItemProps {
  item: AuthenticatedSidebarItemV2;
  isActive: boolean;
  isCollapsed?: boolean;
}

interface AuthenticatedSidebarProps {
  defaultCollapsed?: boolean;
  user?: AuthenticatedLayoutUserV2;
}

interface AuthenticatedMobileTopBarProps {
  user?: AuthenticatedLayoutUserV2;
}

function navItemClassName(isActive: boolean, isCollapsed = false): string {
  return cn(
    "flex items-center rounded-[8px] border text-left text-[13px] font-medium transition-colors focus:outline-none focus-visible:outline-none focus-visible:ring-0",
    isCollapsed
      ? "size-10 justify-center"
      : "w-full gap-[9px] px-[11px] py-[9px]",
    isActive
      ? "border-border bg-background font-semibold text-foreground"
      : "border-transparent text-muted-foreground hover:bg-background hover:text-foreground",
  );
}

function NavItemContent({ item, isActive, isCollapsed }: NavItemProps) {
  const Icon = item.icon;

  return (
    <>
      <Icon
        className={cn(
          "size-4",
          isActive ? "text-primary" : "text-muted-foreground",
        )}
        aria-hidden="true"
      />
      <span className={cn(isCollapsed && "sr-only")}>{item.label}</span>
    </>
  );
}

function DesktopNavItem({ item, isActive, isCollapsed }: NavItemProps) {
  const navItem = item.href ? (
    <Link
      to={item.href}
      className={navItemClassName(isActive, isCollapsed)}
      aria-current={isActive ? "page" : undefined}
      onClick={item.onSelect}
    >
      <NavItemContent
        item={item}
        isActive={isActive}
        isCollapsed={isCollapsed}
      />
    </Link>
  ) : (
    <button
      type="button"
      className={navItemClassName(isActive, isCollapsed)}
      aria-current={isActive ? "page" : undefined}
      onClick={item.onSelect}
    >
      <NavItemContent
        item={item}
        isActive={isActive}
        isCollapsed={isCollapsed}
      />
    </button>
  );

  if (!isCollapsed) {
    return navItem;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{navItem}</TooltipTrigger>
      <TooltipContent side="right">{item.label}</TooltipContent>
    </Tooltip>
  );
}

function MobileNavItem({ item, isActive }: NavItemProps) {
  if (item.href) {
    return (
      <SheetClose asChild>
        <Link
          to={item.href}
          className={navItemClassName(isActive)}
          aria-current={isActive ? "page" : undefined}
          onClick={item.onSelect}
        >
          <NavItemContent item={item} isActive={isActive} />
        </Link>
      </SheetClose>
    );
  }

  return (
    <SheetClose asChild>
      <button
        type="button"
        className={navItemClassName(isActive)}
        aria-current={isActive ? "page" : undefined}
        onClick={item.onSelect}
      >
        <NavItemContent item={item} isActive={isActive} />
      </button>
    </SheetClose>
  );
}

function useIsActiveNavItem() {
  const { pathname } = useLocation();

  return (item: AuthenticatedSidebarItemV2) => item.href === pathname;
}

interface BrandProps {
  className?: string;
  isCollapsed?: boolean;
}

function Brand({ className, isCollapsed = false }: BrandProps) {
  return (
    <div
      className={cn(
        "flex items-center gap-2 px-4 py-4",
        isCollapsed && "justify-center gap-0 px-0",
        className,
      )}
    >
      <span className="size-[9px] rounded-full bg-primary" aria-hidden="true" />
      <span
        className={cn(
          "font-mono text-[12.5px] font-semibold tracking-[0.03em] text-foreground",
          isCollapsed && "sr-only",
        )}
      >
        {PRODUCT_NAME.singular}
      </span>
    </div>
  );
}

function DesktopSidebarHeader({
  isCollapsed,
  onToggle,
}: {
  isCollapsed: boolean;
  onToggle: () => void;
}) {
  return (
    <div
      className={cn(
        "flex items-center py-3",
        isCollapsed ? "flex-col gap-2 px-0" : "justify-between px-3",
      )}
    >
      <Brand isCollapsed={isCollapsed} className="px-0 py-0" />
      <button
        type="button"
        className="inline-flex size-8 items-center justify-center rounded-[8px] border border-border bg-background text-muted-foreground transition-colors hover:text-foreground focus:outline-none focus-visible:outline-none focus-visible:ring-0"
        aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        aria-expanded={!isCollapsed}
        onClick={onToggle}
      >
        <Menu className="size-4" aria-hidden="true" />
      </button>
    </div>
  );
}

function UserSummary({
  isCollapsed = false,
  user,
}: {
  isCollapsed?: boolean;
  user: AuthenticatedLayoutUserV2;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const visitTour = useTourGuide(VISIT_TOUR_ID);
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout(queryClient);
    navigate(navigationV2.login(), { replace: true });
  };

  const handleStartTour = () => {
    setIsAccountMenuOpen(false);
    visitTour.startTour();
  };

  return (
    <Popover open={isAccountMenuOpen} onOpenChange={setIsAccountMenuOpen}>
      <div
        className={cn(
          "mt-auto border-t border-border py-3",
          isCollapsed ? "px-0" : "px-3",
        )}
      >
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            className={cn(
              "h-auto rounded-[8px] p-0 text-left hover:bg-background focus-visible:ring-0",
              isCollapsed
                ? "mx-auto flex size-8 justify-center"
                : "w-full justify-start gap-2 px-0",
            )}
            aria-label={`Open account menu for ${user.name}`}
            title={`${user.name}${user.subtitle ? `, ${user.subtitle}` : ""}`}
          >
            <Avatar className="size-7 shrink-0 border border-border bg-background">
              <AvatarFallback className="bg-background font-mono text-[10px] text-muted-foreground">
                {user.initials}
              </AvatarFallback>
            </Avatar>
            <div
              className={cn("min-w-0 leading-tight", isCollapsed && "sr-only")}
            >
              <p className="truncate text-xs font-semibold text-foreground">
                {user.name}
              </p>
              {user.subtitle ? (
                <p className="truncate font-mono text-[9.5px] text-muted-foreground">
                  {user.subtitle}
                </p>
              ) : null}
            </div>
          </Button>
        </PopoverTrigger>
      </div>
      <PopoverContent
        side="top"
        align="start"
        sideOffset={8}
        aria-label="Account menu"
        className="w-56 rounded-[10px] p-2"
      >
        <div className="flex items-center gap-2 px-2 py-2">
          <Avatar className="size-8 border border-border bg-background">
            <AvatarFallback className="bg-background font-mono text-[10px] text-muted-foreground">
              {user.initials}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 leading-tight">
            <p className="truncate text-xs font-semibold text-foreground">
              {user.name}
            </p>
            {user.subtitle ? (
              <p className="truncate font-mono text-[10px] text-muted-foreground">
                {user.subtitle}
              </p>
            ) : null}
          </div>
        </div>
        <div className="my-1 h-px bg-border" />
        <div className="space-y-0.5">
          <Button
            asChild
            variant="ghost"
            className="h-8 w-full justify-start gap-2 rounded-[7px] px-2 text-xs font-medium"
          >
            <Link to={navigationV2.settings()}>
              <Settings className="size-3.5" aria-hidden="true" />
              Settings
            </Link>
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="h-8 w-full justify-start gap-2 rounded-[7px] px-2 text-xs font-medium"
            onClick={handleStartTour}
          >
            <Sparkles className="size-3.5" aria-hidden="true" />
            Test visit
          </Button>
          <Button
            asChild
            variant="ghost"
            className="h-8 w-full justify-start gap-2 rounded-[7px] px-2 text-xs font-medium"
          >
            <a href="mailto:support@elationemr.com">
              <CircleHelp className="size-3.5" aria-hidden="true" />
              Support
            </a>
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="h-8 w-full justify-start gap-2 rounded-[7px] px-2 text-xs font-medium text-destructive hover:text-destructive"
            onClick={handleLogout}
          >
            <LogOut className="size-3.5" aria-hidden="true" />
            Log Out
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function AuthenticatedMobileTopBar({
  user,
}: AuthenticatedMobileTopBarProps) {
  const isActiveNavItem = useIsActiveNavItem();

  return (
    <div className="flex shrink-0 items-center justify-between border-b border-border bg-surface px-4 py-3 md-stitch:hidden">
      <Brand />
      <Sheet>
        <SheetTrigger asChild>
          <button
            type="button"
            className="inline-flex size-9 items-center justify-center rounded-[8px] border border-border bg-background text-muted-foreground transition-colors hover:text-foreground focus:outline-none focus-visible:outline-none focus-visible:ring-0"
            aria-label="Open navigation menu"
          >
            <Menu className="size-4" aria-hidden="true" />
          </button>
        </SheetTrigger>
        <SheetContent
          side="left"
          className="flex w-[188px] flex-col bg-surface p-0"
        >
          <SheetHeader className="text-left">
            <SheetTitle className="sr-only">Navigation</SheetTitle>
            <Brand />
          </SheetHeader>
          <nav className="space-y-0.5 px-[10px] py-1" aria-label="Primary">
            {routes.map((item) => (
              <MobileNavItem
                key={item.id}
                item={item}
                isActive={isActiveNavItem(item)}
              />
            ))}
          </nav>
          {user ? <UserSummary user={user} /> : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}

export function AuthenticatedSidebar({
  defaultCollapsed = false,
  user,
}: AuthenticatedSidebarProps) {
  const isActiveNavItem = useIsActiveNavItem();
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);

  return (
    <aside
      className={cn(
        "hidden min-h-0 shrink-0 flex-col overflow-hidden border-r border-border bg-surface transition-[width] duration-200 ease-in-out md-stitch:flex",
        isCollapsed ? "w-[52px]" : "w-[188px]",
      )}
    >
      <TooltipProvider delayDuration={100}>
        <DesktopSidebarHeader
          isCollapsed={isCollapsed}
          onToggle={() => setIsCollapsed((collapsed) => !collapsed)}
        />
        <nav
          className={cn(
            "space-y-0.5 py-1",
            isCollapsed ? "px-1.5" : "px-[10px]",
          )}
          aria-label="Primary"
        >
          {routes.map((item) => (
            <DesktopNavItem
              key={item.id}
              item={item}
              isActive={isActiveNavItem(item)}
              isCollapsed={isCollapsed}
            />
          ))}
        </nav>
        {user ? <UserSummary user={user} isCollapsed={isCollapsed} /> : null}
      </TooltipProvider>
    </aside>
  );
}
